import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Image as ImageIcon, Sparkles, Download } from "lucide-react";
import { useTr } from "@/i18n/translator";

export default function Images() {
  const tr = useTr();
  const [images, setImages] = useState([]);
  const [prompt, setPrompt] = useState("");
  const [loading, setLoading] = useState(false);

  const load = () => api.get("/images").then(({ data }) => setImages(data));
  useEffect(() => { load(); }, []);

  const generate = async () => {
    if (!prompt.trim()) return toast.error(tr("Escribe un prompt"));
    setLoading(true);
    try {
      await api.post("/generate/image", { prompt, brand_aware: true });
      toast.success("Imagen generada");
      setPrompt("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="images-page">
      <div>
        <div className="font-mono-mark text-[10px] text-[#5C5C5C]">{tr("ARCHIVO VISUAL · IA DE IMAGEN")}</div>
        <h1 className="font-display text-4xl sm:text-5xl mt-2">{tr("Banco de imágenes IA")}</h1>
      </div>

      <div className="bg-white border border-[#0A0A0A] p-6 flex flex-col sm:flex-row gap-3">
        <input
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder={tr("Describe la imagen: fondo minimalista, producto en primer plano…")}
          className="flex-1 bg-white border border-[#0A0A0A] px-4 py-3 font-mono-data text-sm focus:outline-none focus:ring-2 focus:ring-[#E53935]"
          data-testid="images-prompt"
        />
        <button
          onClick={generate}
          disabled={loading}
          className="bg-[#0A0A0A] text-white px-4 py-3 font-mono-mark text-xs hover-brutal border border-[#0A0A0A] flex items-center justify-center gap-2 disabled:opacity-50"
          data-testid="images-generate"
        >
          <Sparkles className="w-4 h-4" />
          {loading ? "GENERANDO…" : "GENERAR"}
        </button>
      </div>

      {images.length === 0 ? (
        <div className="bg-white border border-dashed border-[#0A0A0A] p-10 text-center">
          <ImageIcon className="w-8 h-8 mx-auto text-[#5C5C5C]" />
          <div className="font-mono-mark text-[10px] text-[#5C5C5C] mt-3">{tr("ARCHIVO VACÍO")}</div>
          <div className="text-sm mt-1">{tr("Aún no has generado imágenes.")}</div>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {images.map((img) => (
            <div key={img.id} className="bg-white border border-[#0A0A0A] hover-brutal overflow-hidden" data-testid={`image-${img.id}`}>
              <img src={`data:image/png;base64,${img.image_base64}`} alt={img.prompt} className="w-full aspect-square object-cover" />
              <div className="p-3 border-t border-[#0A0A0A]">
                <p className="text-xs line-clamp-2">{img.prompt}</p>
                <a
                  href={`data:image/png;base64,${img.image_base64}`}
                  download={`cia-${img.id}.png`}
                  className="mt-2 inline-flex items-center gap-1 font-mono-mark text-[9px] text-[#E53935]"
                  data-testid={`image-download-${img.id}`}
                >
                  <Download className="w-3 h-3" /> {tr("DESCARGAR")}
                </a>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
